import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import MainButton from '../../../shared/layout/buttons/MainButton';
import AdminFilterBar from './components/AdminFilterBar';
import AdminDataTable from './components/AdminDataTable';
import {
  fetchAdminScraperStagingBatch,
  processAdminScraperStagingBatch,
  updateAdminScraperStagingRow,
} from '../../../services/Http/AdminApi';

const AdminScraperStagingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [batch, setBatch] = useState(null);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchAdminScraperStagingBatch(id, { status, per_page: 100 });
      setBatch(response?.data || null);
      setRows(response?.rows?.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load staging batch');
    } finally {
      setLoading(false);
    }
  }, [id, status]);

  useEffect(() => {
    load();
  }, [load]);

  const setRowStatus = async (row, nextStatus) => {
    try {
      await updateAdminScraperStagingRow(id, row.id, { status: nextStatus });
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update row');
    }
  };

  const processBatch = async () => {
    setProcessing(true);
    try {
      await processAdminScraperStagingBatch(id, {
        create_missing_manufacturers: true,
        create_missing_items: true,
      });
      toast.success('Batch processing completed');
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to process batch');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <PageContainer>
      <PageHeader>
        <HeaderRow>
          <PageTitle>Scraper Batch Detail</PageTitle>
          <HeaderActions>
            <MainButton size="small" onClick={() => navigate('/scraper-staging')}>Back</MainButton>
            <MainButton size="small" onClick={load}>Refresh</MainButton>
            <MainButton size="small" disabled={processing} onClick={processBatch}>
              {processing ? 'Processing...' : 'Process Approved Rows'}
            </MainButton>
          </HeaderActions>
        </HeaderRow>
      </PageHeader>
      <PageContent>
        <MetaGrid>
          <MetaCard><MetaLabel>Batch ID</MetaLabel><MetaValue><code>{batch?.id || '—'}</code></MetaValue></MetaCard>
          <MetaCard><MetaLabel>Organization</MetaLabel><MetaValue>{batch?.organization_name || '—'}</MetaValue></MetaCard>
          <MetaCard><MetaLabel>Source</MetaLabel><MetaValue>{batch?.source_name || '—'}</MetaValue></MetaCard>
          <MetaCard><MetaLabel>Status</MetaLabel><MetaValue>{batch?.status || '—'}</MetaValue></MetaCard>
          <MetaCard><MetaLabel>Total Rows</MetaLabel><MetaValue>{batch?.total_rows || 0}</MetaValue></MetaCard>
          <MetaCard><MetaLabel>Approved</MetaLabel><MetaValue>{batch?.approved_rows || 0}</MetaValue></MetaCard>
        </MetaGrid>

        <AdminFilterBar>
          <FilterRow>
            <Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All row statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="processed">Processed</option>
              <option value="failed">Failed</option>
            </Select>
          </FilterRow>
        </AdminFilterBar>

        <AdminDataTable
          loading={loading}
          rows={rows}
          emptyText="No rows found"
          columns={[
            { key: 'row_number', label: 'Row' },
            { key: 'status', label: 'Status' },
            {
              key: 'manufacturer_name',
              label: 'Manufacturer',
              render: (r) => r.normalized_payload?.manufacturer_name || r.raw_payload?.manufacturer_name || r.raw_payload?.brand || '—',
            },
            {
              key: 'product_name',
              label: 'Product',
              render: (r) => r.normalized_payload?.product_name || r.raw_payload?.product_name || r.raw_payload?.name || '—',
            },
            {
              key: 'sku',
              label: 'SKU/GTIN',
              render: (r) => r.normalized_payload?.sku_or_gtin || r.raw_payload?.sku_or_gtin || r.raw_payload?.sku || r.raw_payload?.gtin || '—',
            },
            {
              key: 'discount',
              label: 'Discount',
              render: (r) => r.normalized_payload?.discount_percentage || r.raw_payload?.discount_percentage || r.raw_payload?.discount_value || '—',
            },
            {
              key: 'actions',
              label: 'Actions',
              render: (r) => (
                <Actions>
                  <ActionButton onClick={() => setRowStatus(r, 'approved')}>Approve</ActionButton>
                  <ActionButton $tone="danger" onClick={() => setRowStatus(r, 'rejected')}>Reject</ActionButton>
                </Actions>
              ),
            },
          ]}
        />
      </PageContent>
    </PageContainer>
  );
};

export default AdminScraperStagingDetail;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;
const HeaderActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;
const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
`;
const MetaCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  padding: 0.75rem;
  background: ${({ theme }) => theme.colors.surface};
`;
const MetaLabel = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;
const MetaValue = styled.div`
  margin-top: 0.375rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
`;
const FilterRow = styled.div`
  display: flex;
  gap: 0.625rem;
  align-items: center;
`;
const Select = styled.select`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 10px;
  padding: 0.5625rem 0.75rem;
  background: ${({ theme }) => theme.colors.surface};
`;
const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
`;
const ActionButton = styled.button`
  border: 1px solid ${({ theme, $tone }) => $tone === 'danger' ? theme.colors.danger : theme.colors.border};
  background: ${({ theme, $tone }) => $tone === 'danger' ? theme.colors.dangerBg : theme.colors.surface};
  color: ${({ theme, $tone }) => $tone === 'danger' ? theme.colors.danger : theme.colors.textPrimary};
  border-radius: 8px;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
`;

